import React, { useState, useMemo } from 'react';

import PublicScheduleLayout from '../PublicSchedule/components/PublicScheduleLayout';
import PublicScheduleHeader from '../PublicSchedule/components/PublicScheduleHeader';
import TrustFooter from '../PublicSchedule/components/TrustFooter';

import '../PublicSchedule/PublicSchedulePage.css';
import './PublicAppointmentConfirmationPage.css';

/**
 * PublicAppointmentConfirmationDemo
 *
 * Standalone demo/preview page for testing the V3 reschedule slot picker UI.
 * Uses hardcoded mock data — NO backend calls, NO real tokens.
 *
 * Route: /confirmar-demo
 *
 * Safe to leave in the codebase — it's an isolated route with zero impact
 * on real flows. Remove the route from App.js when no longer needed.
 */

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_CLINIC = {
    id: '999',
    name: 'Clínica Demo',
    logo: '',
    city: 'São Paulo',
    state: 'SP',
    phone: '11999999999',
    whatsapp: '11999999999',
};

const MOCK_APPOINTMENT = {
    clinic: MOCK_CLINIC,
    clinic_name: 'Clínica Demo',
    professional_name: 'Dr. João Silva',
    procedure_name: 'Consulta Geral',
    patient_first_name: 'Maria',
    patient_name: 'Maria Santos',
    date: getFutureDate(3),   // 3 dias no futuro
    time: '14:30',
    company_slug: 'demo',
    professional_id: '1',
    procedure_duration: 50,
};

const MOCK_SLOTS = [
    { date: getFutureDate(4), time: '09:00' },
    { date: getFutureDate(4), time: '10:30' },
    { date: getFutureDate(5), time: '08:00' },
    { date: getFutureDate(5), time: '14:00' },
    { date: getFutureDate(7), time: '11:00' },
    { date: getFutureDate(7), time: '16:30' },
];

/** Returns YYYY-MM-DD for N days from today */
function getFutureDate(daysAhead) {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

// ─── Date formatting (duplicated from main page to keep demo self-contained) ─

const WEEKDAYS_LONG = ['domingo','segunda-feira','terça-feira','quarta-feira','quinta-feira','sexta-feira','sábado'];
const WEEKDAYS_SHORT = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const MONTHS = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];

function parseIsoDate(iso) {
    if (!iso) return null;
    const [y,m,d] = iso.split('-').map(Number);
    return new Date(y, m-1, d);
}
function formatShortDate(iso) {
    const dt = parseIsoDate(iso);
    if (!dt) return iso || '';
    return `${WEEKDAYS_SHORT[dt.getDay()]}, ${String(dt.getDate()).padStart(2,'0')} de ${MONTHS[dt.getMonth()]}`;
}
function formatFullDate(iso) {
    const dt = parseIsoDate(iso);
    if (!dt) return iso || '';
    return `${WEEKDAYS_LONG[dt.getDay()]}, ${dt.getDate()} de ${MONTHS[dt.getMonth()]} de ${dt.getFullYear()}`;
}

// ─── Icons (minimal set for demo) ────────────────────────────────────────────

const IconCalendar = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconClock = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconStethoscope = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M6 3v6a4 4 0 0 0 8 0V3"/><path d="M10 13v2a4 4 0 0 0 8 0"/><circle cx="18" cy="17" r="2"/></svg>;
const IconUser = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconCalendarCheck = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 15 11 17 15 13"/></svg>;
const IconCheckCircle = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><polyline points="8 12 11 15 16 9"/></svg>;
const IconRefresh = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><polyline points="21 3 21 8 16 8"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><polyline points="3 21 3 16 8 16"/></svg>;
const IconXCircle = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
const IconLock = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const IconArrowLeft = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>;
const IconInfo = (p) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>;
const IconWhatsapp = (p) => <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347z"/><path d="M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.304-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.52 3.449M12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/></svg>;

// ─── Demo screens ────────────────────────────────────────────────────────────

const SCREENS = [
    { id: 'home',       label: '1. Tela inicial' },
    { id: 'confirm',    label: '2. Confirmar presença' },
    { id: 'slots',      label: '3. Reagendar (V3)' },
    { id: 'slots-empty',label: '4. Reagendar (vazio)' },
    { id: 'success',    label: '5. Sucesso' },
];

export default function PublicAppointmentConfirmationDemo() {
    const [screen, setScreen] = useState('home');
    const [selectedSlot, setSelectedSlot] = useState(null);

    const appt = MOCK_APPOINTMENT;
    const clinic = MOCK_CLINIC;
    const shortDate = formatShortDate(appt.date);
    const fullDate = formatFullDate(appt.date);
    const time = appt.time;

    const slotsByDate = useMemo(() => {
        const groups = {};
        for (const s of MOCK_SLOTS) {
            if (!groups[s.date]) groups[s.date] = [];
            groups[s.date].push(s);
        }
        return Object.entries(groups);
    }, []);

    const isSlotSelected = (s) =>
        selectedSlot && selectedSlot.date === s.date && selectedSlot.time === s.time;

    return (
        <PublicScheduleLayout>
            <PublicScheduleHeader company={{ name: clinic.name, logo: clinic.logo, city: clinic.city, state: clinic.state }} />

            {/* ── Demo nav bar ─────────────────────────────────────────── */}
            <div style={{
                background: '#1a1a2e', color: '#fff', padding: '10px 16px',
                display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center',
            }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#f0c040', width: '100%', textAlign: 'center', marginBottom: 4 }}>
                    DEMO MODE — Dados fictícios
                </span>
                {SCREENS.map(s => (
                    <button
                        key={s.id}
                        onClick={() => { setScreen(s.id); setSelectedSlot(null); }}
                        style={{
                            padding: '5px 10px', fontSize: 11, fontWeight: screen === s.id ? 700 : 400,
                            background: screen === s.id ? '#f0c040' : '#2d2d4e',
                            color: screen === s.id ? '#1a1a2e' : '#ccc',
                            border: 'none', borderRadius: 6, cursor: 'pointer',
                        }}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <main className="pac-main">

                {/* ── 1. Home (3 CTAs) ──────────────────────────────────── */}
                {screen === 'home' && (
                    <>
                        <div className="pac-intro">
                            <div className="pac-intro__badge"><IconCalendarCheck width="22" height="22" /></div>
                            <div className="pac-intro__text-wrap">
                                <h1 className="pac-intro__title">Olá, {appt.patient_first_name}!</h1>
                                <p className="pac-intro__text">Confirme se você poderá comparecer ao seu agendamento.</p>
                            </div>
                        </div>
                        <AppointmentGrid appt={appt} shortDate={shortDate} fullDate={fullDate} time={time} />
                        <div className="pac-cta-stack">
                            <button className="pac-cta pac-cta--primary" onClick={() => setScreen('confirm')}>
                                <span className="pac-cta__icon"><IconCheckCircle width="24" height="24" /></span>
                                <span className="pac-cta__text"><span className="pac-cta__title">Confirmar presença</span><span className="pac-cta__subtitle">Eu irei comparecer normalmente</span></span>
                            </button>
                            <button className="pac-cta pac-cta--secondary" onClick={() => setScreen('slots')}>
                                <span className="pac-cta__icon"><IconRefresh width="22" height="22" /></span>
                                <span className="pac-cta__text"><span className="pac-cta__title">Solicitar reagendamento</span><span className="pac-cta__subtitle">Preciso de outro horário</span></span>
                            </button>
                            <button className="pac-cta pac-cta--danger-outline" onClick={() => alert('Demo: cancelamento')}>
                                <span className="pac-cta__icon"><IconXCircle width="24" height="24" /></span>
                                <span className="pac-cta__text"><span className="pac-cta__title">Preciso cancelar</span><span className="pac-cta__subtitle">Não poderei comparecer</span></span>
                            </button>
                        </div>
                        <p className="pac-security-note"><IconLock width="12" height="12" /> Suas informações estão seguras e esta ação será registrada automaticamente.</p>
                    </>
                )}

                {/* ── 2. Confirm step ──────────────────────────────────── */}
                {screen === 'confirm' && (
                    <div className="pac-confirm pac-confirm--confirm">
                        <button className="pac-confirm__back" onClick={() => setScreen('home')}><IconArrowLeft width="20" height="20" /></button>
                        <div className="pac-confirm__icon pac-confirm__icon--confirm"><IconCheckCircle width="40" height="40" /></div>
                        <h2 className="pac-confirm__title">Você tem certeza que deseja confirmar sua presença?</h2>
                        <p className="pac-confirm__text">Avisaremos que você irá comparecer normalmente no horário marcado.</p>
                        <AppointmentGrid appt={appt} shortDate={shortDate} fullDate={fullDate} time={time} />
                        <div className="pac-confirm__actions">
                            <button className="pac-btn pac-btn--solid pac-btn--confirm" onClick={() => setScreen('success')}>Sim, confirmar presença</button>
                            <button className="pac-btn pac-btn--ghost" onClick={() => setScreen('home')}>Voltar</button>
                        </div>
                    </div>
                )}

                {/* ── 3. Slot Picker (V3 — with slots) ──────────────── */}
                {screen === 'slots' && (
                    <div className="pac-slot-picker">
                        <button className="pac-confirm__back" onClick={() => setScreen('home')}><IconArrowLeft width="20" height="20" /></button>
                        <div className="pac-confirm__icon pac-confirm__icon--reschedule"><IconRefresh width="40" height="40" /></div>
                        <h2 className="pac-slot-picker__title">Escolha uma opção de horário</h2>
                        <p className="pac-slot-picker__subtitle">
                            Essas são algumas possibilidades próximas para facilitar o reagendamento.
                            A disponibilidade final será confirmada pelo WhatsApp.
                        </p>

                        <div className="pac-slot-picker__list">
                            {slotsByDate.map(([date, dateSlots]) => (
                                <div key={date} className="pac-slot-picker__date-group">
                                    <div className="pac-slot-picker__date-label">
                                        <IconCalendar width="14" height="14" />
                                        {formatShortDate(date)}
                                    </div>
                                    <div className="pac-slot-picker__time-chips">
                                        {dateSlots.map((slot) => (
                                            <button
                                                key={`${slot.date}-${slot.time}`}
                                                type="button"
                                                className={`ps-slot${isSlotSelected(slot) ? ' ps-slot--selected' : ''}`}
                                                onClick={() => setSelectedSlot(slot)}
                                            >
                                                {slot.time}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="pac-slot-picker__actions">
                            <button
                                className="pac-btn pac-btn--solid pac-btn--reschedule pac-slot-picker__confirm-btn"
                                disabled={!selectedSlot}
                                onClick={() => {
                                    alert(`Demo: WhatsApp abriria com slot ${selectedSlot.date} ${selectedSlot.time}`);
                                    setScreen('success');
                                }}
                            >
                                <IconWhatsapp width="18" height="18" />
                                Pedir este horário pelo WhatsApp
                            </button>
                            <button
                                className="pac-btn pac-btn--ghost pac-slot-picker__skip-btn"
                                onClick={() => {
                                    alert('Demo: WhatsApp abriria sem slot (mensagem genérica)');
                                    setScreen('success');
                                }}
                            >
                                <IconWhatsapp width="16" height="16" />
                                Prefiro combinar outro horário
                            </button>
                        </div>

                        <p className="pac-security-note">
                            <IconInfo width="12" height="12" />
                            Selecionar um horário não reserva a vaga. A disponibilidade será confirmada pelo WhatsApp.
                        </p>
                    </div>
                )}

                {/* ── 5. Slot Picker (empty state) ─────────────────────── */}
                {screen === 'slots-empty' && (
                    <div className="pac-slot-picker">
                        <button className="pac-confirm__back" onClick={() => setScreen('home')}><IconArrowLeft width="20" height="20" /></button>
                        <div className="pac-confirm__icon pac-confirm__icon--reschedule"><IconRefresh width="40" height="40" /></div>
                        <h2 className="pac-slot-picker__title">Escolha uma opção de horário</h2>
                        <p className="pac-slot-picker__subtitle">
                            Essas são algumas possibilidades próximas para facilitar o reagendamento.
                            A disponibilidade final será confirmada pelo WhatsApp.
                        </p>

                        <div className="pac-slot-picker__empty">
                            <IconCalendar width="32" height="32" />
                            <p>Não encontramos horários próximos disponíveis para sugestão.</p>
                            <p className="pac-slot-picker__empty-hint">
                                Toque abaixo para entrar em contato e combinar um novo horário.
                            </p>
                        </div>

                        <div className="pac-slot-picker__actions">
                            <button
                                className="pac-btn pac-btn--solid pac-btn--reschedule pac-slot-picker__skip-btn"
                                onClick={() => alert('Demo: WhatsApp abriria (fallback)')}
                            >
                                <IconWhatsapp width="16" height="16" />
                                Entrar em contato
                            </button>
                        </div>
                    </div>
                )}

                {/* ── 6. Success screen ────────────────────────────────── */}
                {screen === 'success' && (
                    <div className="pac-success">
                        <div className="pac-success__icon"><IconCheckCircle width="34" height="34" /></div>
                        <h2 className="pac-success__title">Tudo certo!</h2>
                        <p className="pac-success__subtitle">Sua resposta foi registrada com sucesso.</p>
                        <div className="pac-success__pill-wrap">
                            <div className="pac-success__summary-label">Sua resposta</div>
                            <span className="pac-pill pac-pill--warn"><IconRefresh width="14" height="14" /> Reagendamento solicitado</span>
                        </div>
                        <AppointmentGrid appt={appt} shortDate={shortDate} fullDate={fullDate} time={time} />
                        <p className="pac-success__hint"><IconInfo width="14" height="14" /> Não abriu o WhatsApp? Toque aqui para enviar sua mensagem:</p>
                        <button className="pac-whatsapp-btn" onClick={() => alert('Demo: WhatsApp reabriria')}>
                            <span className="pac-whatsapp-btn__icon"><IconWhatsapp width="20" height="20" /></span>
                            <span>Abrir WhatsApp novamente</span>
                        </button>
                    </div>
                )}

            </main>
            <TrustFooter />
        </PublicScheduleLayout>
    );
}

// ─── Mini AppointmentGrid (self-contained for demo) ──────────────────────────

function AppointmentGrid({ appt, shortDate, fullDate, time }) {
    return (
        <section className="pac-card">
            <div className="pac-card__row pac-card__row--split">
                <div className="pac-item" title={fullDate}>
                    <span className="pac-item__icon pac-item__icon--pink"><IconCalendar width="26" height="26" /></span>
                    <div className="pac-item__body"><div className="pac-item__label">Data</div><div className="pac-item__value">{shortDate}</div></div>
                </div>
                <div className="pac-item">
                    <span className="pac-item__icon pac-item__icon--pink"><IconClock width="26" height="26" /></span>
                    <div className="pac-item__body"><div className="pac-item__label">Horário</div><div className="pac-item__value">{time}</div></div>
                </div>
            </div>
            {appt.procedure_name && (
                <div className="pac-card__row"><div className="pac-item">
                    <span className="pac-item__icon pac-item__icon--muted"><IconStethoscope width="26" height="26" /></span>
                    <div className="pac-item__body"><div className="pac-item__label">Procedimento</div><div className="pac-item__value">{appt.procedure_name}</div></div>
                </div></div>
            )}
            {appt.professional_name && (
                <div className="pac-card__row"><div className="pac-item">
                    <span className="pac-item__icon pac-item__icon--muted"><IconUser width="26" height="26" /></span>
                    <div className="pac-item__body"><div className="pac-item__label">Profissional</div><div className="pac-item__value">{appt.professional_name}</div></div>
                </div></div>
            )}
        </section>
    );
}
